import React from "react";
import { useTranslations } from "use-intl";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from "@/utils/paymentUtils";
import { ArrowUp, ArrowDown } from "lucide-react";
import { useTableSort } from "@/hooks/useTableSort";
import { cn } from "@/lib/utils";

interface CurrencyAccount {
  id: string;
  currency: string;
  amount: number;
  symbol: string;
}

interface CurrencyAccountsListProps {
  currencyAccounts: CurrencyAccount[];
}

const CurrencyAccountsList: React.FC<CurrencyAccountsListProps> = ({ currencyAccounts }) => {
  const t = useTranslations('consultantTransactions.dashboard');
  const { sortedData, sortConfig, handleSort } = useTableSort<CurrencyAccount, keyof CurrencyAccount>(currencyAccounts);

  const renderSortIcon = (key: keyof CurrencyAccount) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4 inline" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4 inline" />
    );
  };

  return (
    <Card className="flex flex-col light-blue-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {t('currencyAccounts.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow">
        {currencyAccounts.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            {t('currencyAccounts.empty')}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className={cn("cursor-pointer", sortConfig.key === 'currency' && "text-primary")}
                  onClick={() => handleSort('currency')}
                >
                  {t('currencyAccounts.currency')} {renderSortIcon('currency')}
                </TableHead>
                <TableHead 
                  className={cn("cursor-pointer", sortConfig.key === 'amount' && "text-primary")}
                  onClick={() => handleSort('amount')}
                >
                  {t('currencyAccounts.amount')} {renderSortIcon('amount')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map(account => (
                <TableRow key={account.id}>
                  <TableCell className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border border-muted bg-background">
                      <span className="text-sm font-bold">{account.symbol}</span>
                    </div>
                    <span>{account.currency}</span>
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatCurrency(account.amount, account.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <CardFooter className="text-xs text-muted-foreground mt-auto pt-2 border-t">
        {t('lastUpdated', { minutes: 15 })}
      </CardFooter>
    </Card>
  );
};

export default CurrencyAccountsList;
